import { store } from "@/store"
import { MouseCursor } from "."
import {
  _MSG_CODE_CLEAR_CURSOR_CACHE,
  _MSG_CODE_SET_CURSOR_DATA,
  _MSG_CODE_SET_CURSOR_ID,
} from "./constants"
import { geelevel } from "./proto_message"
import { setIsThreeDMouse } from "@/stores/remote_desk_state_slice"

export type OnCursorChange = (cursor: MouseCursor) => void
let isThreeDMouse = false

// eslint-disable-next-line @typescript-eslint/naming-convention
export class _CursorEventProcessor {
  // 鼠标样式变化时触发的回调
  private onCursorChange: OnCursorChange | null = null

  // 根据 id 保存鼠标样式图片
  readonly cursorMap = new Map<number, MouseCursor>()

  process(code: number, data?: Uint8Array) {
    if (!data) return
    if (!this.onCursorChange) return

    switch (code) {
      case _MSG_CODE_SET_CURSOR_DATA: {
        const cursor = this.parseCursorSafe(data)
        if (cursor) this.onCursorChange(cursor)
        break
      }
      case _MSG_CODE_SET_CURSOR_ID: {
        let decoded = this.decodeCursorData(data)
        if (!decoded) {
          decoded = this.decodeIdOnly(data)
        }
        if (!decoded) return
        const { id } = decoded
        isThreeDMouse = id === 999
        store.dispatch(setIsThreeDMouse(isThreeDMouse))
        let cursor: MouseCursor
        if (this.cursorMap.has(id as number)) {
          cursor = this.cursorMap.get(id as number)!
        } else {
          const newCursor = this.parseCursorSafe(data)
          if (!newCursor) return
          cursor = newCursor
        }
        this.onCursorChange(cursor)
        break
      }
      case _MSG_CODE_CLEAR_CURSOR_CACHE: {
        this.clear()
        break
      }
    }
  }

  setOnCursorChange(callback: OnCursorChange) {
    this.onCursorChange = callback
  }

  clear() {
    this.cursorMap.clear()
  }

  private parseCursorSafe(cursorData: Uint8Array): MouseCursor | null {
    try {
      return this.parseCursor(cursorData)
    } catch (e) {
      console.error("[Cursor] decode error, fallback placeholder. detail:", e)
      return this.placeholderCursor()
    }
  }

  // 添加对 SetCursorData.decode 的安全封装(try/catch)，出现解码异常时重置缓存、回退到占位光标并打印调试信息。
  // 随后会校验 widthheight4 与 data 长度是否匹配，不匹配则使用占位数据，防止再次抛错。
  private decodeCursorData(cursorData: Uint8Array) {
    try {
      return geelevel.protobuf.SetCursorData.decode(cursorData)
    } catch (e) {
      console.warn(
        "[Cursor] SetCursorData.decode failed:",
        e,
        this.dumpBytes(cursorData)
      )
      return null
    }
  }

  private decodeIdOnly(buf: Uint8Array) {
    try {
      if (buf.length >= 2 && buf[0] === 0x08) {
        let id = 0
        let shift = 0
        for (let i = 1; i < buf.length; i++) {
          id |= (buf[i] & 0x7f) << shift
          if ((buf[i] & 0x80) === 0) {
            return {
              id,
              x: 0,
              y: 0,
              width: 0,
              height: 0,
              data: new Uint8Array(),
            }
          }
          shift += 7
          if (shift > 35) break
        }
      }
      return null
    } catch {
      return null
    }
  }

  private placeholderCursor(): MouseCursor {
    const initWidth = 10
    const initHeight = 10
    const initData = new Uint8ClampedArray(initWidth * initHeight * 4)
    for (let i = 0; i < initData.length; i += 4) {
      initData[i + 0] = 0
      initData[i + 1] = 255
      initData[i + 2] = 0
      initData[i + 3] = 0
    }
    return {
      data: new ImageData(initData, initWidth, initHeight),
      width: initWidth,
      height: initHeight,
      offsetX: 0,
      offsetY: 0,
    }
  }

  private parseCursor(cursorData: Uint8Array): MouseCursor {
    const decoded = this.decodeCursorData(cursorData)
    if (!decoded) throw new Error("decodeCursorData failed")
    const { id, x, y, width, height, data } = decoded
    isThreeDMouse = id === 999
    store.dispatch(setIsThreeDMouse(isThreeDMouse))
    const w = width > 0 ? (width as number) : 10
    const h = height > 0 ? (height as number) : 10
    const byteArr = data as Uint8Array
    const expectLen = w * h * 4
    let pixelData: Uint8ClampedArray
    if (byteArr && byteArr.length === expectLen) {
      pixelData = new Uint8ClampedArray(byteArr)
    } else if (byteArr && byteArr.length > 0 && byteArr.length < expectLen) {
      console.warn(
        `[Cursor] pixel data length mismatch expect=${expectLen} got=${byteArr.length}, padding with transparent.`
      )
      pixelData = new Uint8ClampedArray(expectLen)
      pixelData.set(byteArr)
    } else if (byteArr && byteArr.length > expectLen) {
      console.warn(
        `[Cursor] pixel data length larger than expected expect=${expectLen} got=${byteArr.length}, truncating.`
      )
      pixelData = new Uint8ClampedArray(byteArr.slice(0, expectLen))
    } else {
      console.warn("[Cursor] empty pixel data, using placeholder")
      return this.placeholderCursor()
    }
    const imageData = new ImageData(pixelData, w, h)
    const cursor: MouseCursor = {
      data: imageData,
      width: w,
      height: h,
      offsetX: x,
      offsetY: y,
    }
    this.cursorMap.set(id as number, cursor)
    return cursor
  }

  private dumpBytes(buf: Uint8Array, max: number = 32) {
    const len = Math.min(buf.length, max)
    let hex = ""
    for (let i = 0; i < len; i++) {
      hex += buf[i].toString(16).padStart(2, "0") + " "
    }
    return `len=${buf.length} head=${hex.trim()}`
  }
}
